import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Box,
  Card, CardContent,
  Container,
  IconButton, Table, TableBody, TableCell,
  TableContainer,
  TableHead, TableRow, Typography
} from '@mui/material';
import { useStoreState } from 'pullstate';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BackdropLoader from '../../components/BackdropLoader.jsx';
import { AppStore } from '../../stores/AppStore.js';
import PeopleBuilderFilter from './PeopleBuilderFilter.jsx';
import PeopleBuilderPersonalFilter from './PeopleBuilderPersonalFilter.jsx';
import PeopleBuilderRow from './PeopleBuilderRow.jsx';

const PeopleBuilder = () => {
  const [scrollPlayerId, setScrollPlayerId] = useState([]);
  const [title, setTitle] = useState([]);
  const [pbStatus, setPbStatus] = useState('');
  const [playerNameToFilter, setPlayerNameToFilter] = useState('');
  const [statusToFilter, setStatusToFilter] = useState([]);
  const [yearToFilter, setYearToFilter] = useState('');
  const [filteredPeopleBuilder, setFilteredPeopleBuilder] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);

  const location = useLocation();
  const { strengths, checkIns, peopleBuilders, pbFilterType, loading, goals } = useStoreState(AppStore);
  const cardRef = useRef(0);
  const rowRefs = useRef({});
  const userLogged = useStoreState(AppStore, s => s.user);

  useEffect(() => {
    AppStore.update(s => {
      s.loading = true;
    });
  }, []);

  useEffect(() => {
    if (!openFilter && peopleBuilders?.length) {
      setStatusToFilter([]);
      setPlayerNameToFilter('');

      const active = peopleBuilders.find(pb => pb.pb_settings.status === 'active');

      if (active) {
        setYearToFilter(active.year.toString());
      } else {
        const previousYears = peopleBuilders
          .filter(pb => pb.pb_settings.status === 'previous')
          .map(pb => pb.year);

        setYearToFilter(previousYears.length ? Math.max(...previousYears).toString() : '');
      }
    }
  }, [openFilter, peopleBuilders]); 

  useEffect(() => {
    if (filteredPeopleBuilder?.length) {
      AppStore.update(s => {
        s.loading = false;
      });
    }
  }, [filteredPeopleBuilder]);

  const peopleBuildersFilters = useCallback(() => {
    if (!peopleBuilders?.length) return;

    const applyYearFilter = (list) => {
      if (!yearToFilter) return list;
      return list.filter(user => user.year?.toString() === yearToFilter);
    };

    const applyNameFilter = (list) => {
      if (!playerNameToFilter) return list;
      const q = playerNameToFilter.toLowerCase();
      return list.filter(user => (user.player.full_name || '').toLowerCase().includes(q));
    };

    const applyStatusFilter = (list) => {
      if (!statusToFilter?.length) return list;
      return list.filter(user => statusToFilter.includes(user.pb_settings.status));
    };

    let result = [];

    if (location.pathname.includes('personal')) {
      setTitle('Personal People Builder');

      const personalPeopleBuilder = peopleBuilders.filter(
        user =>
          user.player_id === userLogged.id &&
          !user.player.suspended &&
          user.player.etro_team
      );

      result = personalPeopleBuilder;

    } else if (location.pathname.includes('team')) {
      setTitle('Team People Builder');

      const teamPeopleBuilder = peopleBuilders.filter(
        user =>
          user.player.coach === userLogged.full_name &&
        ['active', 'previous'].includes(user.pb_settings.status) &&
          !user.player.suspended &&
          user.player.etro_team
      );

      teamPeopleBuilder.sort((a, b) => a.player.full_name.localeCompare(b.player.full_name));
      teamPeopleBuilder.sort((a, b) => a.year - b.year);

      result = teamPeopleBuilder;

    } else {
      setTitle('Company People Builder');

      const allPeopleBuilderActive = peopleBuilders.filter(
        user =>
            ['active', 'previous'].includes(user.pb_settings.status) &&
          !user.player.suspended &&
          user.player.etro_team
      );

      allPeopleBuilderActive.sort((a, b) => a.player.full_name.localeCompare(b.player.full_name));

      result = allPeopleBuilderActive;
    }

    // Your pbFilterType title overrides remain (I’m leaving them intact)
    if (pbFilterType === 'personal') {
      setTitle('Personal People Builder');
    } else if (pbFilterType === 'team') {
      setTitle('Team People Builder');
    } else if (pbFilterType === 'all') {
      setTitle('Company People Builder');
    }

    result = applyYearFilter(result);
    result = applyNameFilter(result);
    result = applyStatusFilter(result);

    setFilteredPeopleBuilder(result);
  }, [
    peopleBuilders,
    location.pathname,
    userLogged.id,
    userLogged.full_name,
    pbFilterType,
    yearToFilter,         
    playerNameToFilter,    
    statusToFilter        
  ]);

  const handleScroll = (playerId) => {
    const rowElement = rowRefs.current[playerId];

    if (rowElement) {
      rowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    setScrollPlayerId(localStorage.getItem('playerIdClickedPB'));
    setTimeout(() => {
      scrollingPage();
    }, 1000);
  }, [scrollPlayerId]);

  const scrollingPage = useCallback(() => {
    if (!scrollPlayerId) return;

    const rowElement = rowRefs.current[scrollPlayerId];
    if (rowElement) {
      rowElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      setTimeout(() => {
        const cardElement = cardRef.current;
        cardElement?.scrollBy({ top: -70, behavior: 'smooth' });
      }, 1000);
    }
  }, [scrollPlayerId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem('playerIdClickedPB');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const columns = [
    {
      id: 'filter',
      label: (
        <IconButton
          onClick={() => {
            if (cardRef.current.scrollTop > 0) {
              cardRef.current.scrollTo({ top: 0, behavior: 'smooth' });
              setOpenFilter(true);
            } else {
              setOpenFilter(prev => !prev);
            }
          }}
        >
          <FilterAltIcon />
        </IconButton>
      ),
      minWidth: 50,
      align: 'center',
      format: (value) => <FilterAltIcon sx={{ color: '#000' }} />,
    },
    { id: 'player', label: 'Player', minWidth: 170 },
    { id: 'coach', label: 'Coach', minWidth: 170 },
    { id: 'year', label: 'Year', minWidth: 170 },
    { id: 'status', label: 'Status', minWidth: 170 },
    { id: 'qtyGoals', label: 'Qty of Goals', minWidth: 170 },
    { id: 'date', label: 'Date', minWidth: 170 },
    { id: 'lastCheckIn', label: 'Last Check-In', minWidth: 170 },
  ];
  

  useEffect(() => {
    peopleBuildersFilters();
  }, [peopleBuildersFilters]);

  return (
    <Container maxWidth='xl' sx={{ mt: 10 }} >
      <BackdropLoader openBackdrop={loading} />
      <Card ref={cardRef} sx={{ mt: 4, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', height: 'auto', p: 2, overflow: 'hidden' }}>
          <Box sx={{ flexShrink: 0 }}>
            <Typography sx={{ paddingBottom: '16px' }} variant='h5' align='center'>{title}</Typography>

            {openFilter && (
              title !== 'Personal People Builder' ? (
                <PeopleBuilderFilter
                  openFilter={openFilter}
                  setFilteredPeopleBuilder={setFilteredPeopleBuilder}
                  filteredPeopleBuilder={filteredPeopleBuilder}
                  playerNameToFilter={playerNameToFilter}
                  setPlayerNameToFilter={setPlayerNameToFilter}
                  statusToFilter={statusToFilter}
                  setStatusToFilter={setStatusToFilter}
                  yearToFilter={yearToFilter}
                  setYearToFilter={setYearToFilter}
                  component={title === 'Team People Builder' ? 'team' : 'company'}

                />
              ) : (
                <PeopleBuilderPersonalFilter
                  openFilter={openFilter}
                  setFilteredPeopleBuilder={setFilteredPeopleBuilder}
                  filteredPeopleBuilder={filteredPeopleBuilder}
                />
              )
            )}
          </Box>

          <TableContainer sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0 }} >
            <Table stickyHeader aria-label="collapsible table" >
              <TableHead>
                <TableRow style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ top: 0, position: 'sticky', minWidth: column.minWidth, backgroundColor: '#ccc' }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography sx={{ fontSize: '16px', color: 'darkgray', fontStyle: 'italic' }} variant="h5" color="initial" align='center'>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredPeopleBuilder?.length ? (
                  filteredPeopleBuilder.map((user) => (
                    <PeopleBuilderRow
                      key={user.id}
                      ref={(el) => (rowRefs.current[user.player.id + user.year] = el)}
                      pbStatus={pbStatus}
                      setPbStatus={setPbStatus}
                      playerId={user.player_id}
                      goals={goals}
                      peopleBuilder={user}
                      checkIns={checkIns}
                      handleScroll={() => handleScroll(user.player.id + user.year)}
                      strengths={strengths}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8}>
                    <Typography variant="body2" color="initial" align='center'>No Players Found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

        </CardContent>
      </Card>
    </Container>
  );
};

export default PeopleBuilder;
